// src/components/DepartmentMonthLeaderboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GetDepartmentMonthSummary } from "../API/RequestsServices";

const API_BASE = process.env.REACT_APP_PAYMENT_API || "";

// ---- helpers ----
function formatBHD(n = 0) {
  try {
    const num = Number(n) || 0;
    return `BHD ${num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  } catch {
    return `BHD ${n}`;
  }
}

const MONTHS = [
  { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
  { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
  { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

function buildPicUrl(profilePicPath) {
  if (!profilePicPath) return "";
  const base = API_BASE.replace(/\/+$/, "");
  const path = String(profilePicPath).replace(/^\/+/, "");
  return `${base}/${path}`;
}

function shouldShowRank(u) {
  const paid = Number(u.paidTotal || 0);
  const unpaid = Number(u.unpaidTotal || 0);
  return paid + unpaid > 0; // hide ranks if both are zero
}

function rankBadge(rank) {
  if (rank === 1) {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800">
        🥇 1st
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-800">
        🥈 2nd
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
        🥉 3rd
      </span>
    );
  }
  return null;
}

// Read "ownerOf" from session (handles JSON array or comma string)
function getOwnerOfFromSession() {
  const raw = sessionStorage.getItem("ownerOf");
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
}

/** Compute overlay style so it stretches from table left edge and matches table width, while mounted inside the first cell. */
function useOverlayStretch(tableRef, cellRef) {
  const [style, setStyle] = useState({});
  useEffect(() => {
    function compute() {
      const table = tableRef.current;
      const cell = cellRef.current;
      if (!table || !cell) return;
      const tableRect = table.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const leftOffset = cellRect.left - tableRect.left;
      setStyle({
        position: "absolute",
        left: `${-leftOffset}px`,
        top: 0,
        width: `${tableRect.width}px`,
        height: "100%",
      });
    }
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [tableRef, cellRef]);
  return style;
}

// ---- celebration overlay (kept for non-digital) ----
function CelebrationOverlay({ style }) {
  const pieces = Array.from({ length: 28 }).map((_, i) => {
    const left = (i * 3.3) % 100;
    const delay = (i % 7) * 0.18;
    const duration = 1.8 + ((i * 37) % 60) / 60; // 1.8..2.8s
    const size = 6 + ((i * 13) % 10);           // 6..15px
    const rotate = (i * 47) % 360;
    const hue = (i * 29) % 360;
    return { i, left, delay, duration, size, rotate, hue };
  });

  return (
    <div className="pointer-events-none absolute inset-y-0" style={style}>
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20%) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(120%) rotate(360deg); opacity: 0; }
        }
        @keyframes sparkle { 0%,100% { transform: scale(0.6); opacity: 0; } 40% { transform: scale(1); opacity: 0.9; } 70% { transform: scale(0.8); opacity: 0.4; } }
        @keyframes shimmer { 0% { transform: translateX(-120%); } 100% { transform: translateX(120%); } }
        @keyframes glow-shift { 0% { filter: drop-shadow(0 0 0.5rem rgba(255,215,0,0.3)); } 50% { filter: drop-shadow(0 0 1.25rem rgba(255,215,0,0.6)); } 100% { filter: drop-shadow(0 0 0.5rem rgba(255,215,0,0.3)); } }
      `}</style>

      <div
        className="pointer-events-none absolute inset-0 rounded-md"
        style={{
          padding: 2,
          background: "linear-gradient(135deg, #fde68a, #fca5a5, #93c5fd, #86efac)",
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          animation: "glow-shift 2.6s ease-in-out infinite",
        }}
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
        <div
          className="absolute inset-y-0 w-1/3 opacity-20"
          style={{
            left: 0,
            background:
              "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.65) 50%, rgba(255,255,255,0) 100%)",
            animation: "shimmer 2.2s ease-in-out infinite",
          }}
        />
      </div>

      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
        {pieces.map((p) => (
          <span
            key={p.i}
            className="absolute"
            style={{
              left: `${p.left}%`,
              top: "-10%",
              width: p.size,
              height: p.size * 0.6,
              backgroundColor: `hsl(${p.hue}deg 90% 60%)`,
              transform: `rotate(${p.rotate}deg)`,
              borderRadius: 2,
              animation: `confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
              boxShadow: `0 0 0.25rem hsl(${p.hue}deg 90% 60% / .7)`,
            }}
          />
        ))}
      </div>

      <div className="pointer-events-none absolute left-2 top-1 flex gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            style={{ animation: `sparkle 1.8s ease-in-out ${i * 0.18}s infinite` }}
            className="select-none"
            aria-hidden="true"
          >
            ✨
          </span>
        ))}
      </div>
    </div>
  );
}

/** Row component (kept for non-digital) */
function LeaderboardRow({ u, tableRef }) {
  const pic = buildPicUrl(u.profilePic || u.ProfilePic);
  const showRank = shouldShowRank(u);
  const isChampion = showRank && u._rank === 1;

  const cellRef = useRef(null);
  const overlayStyle = useOverlayStretch(tableRef, cellRef);

  return (
    <tr className="hover:bg-gray-50">
      {/* FIRST CELL: avatar + name + full-width celebration if champion */}
      <td ref={cellRef} className="relative px-4 py-3">
        {isChampion && <CelebrationOverlay style={overlayStyle} />}

        <div className="relative z-[2] flex items-center gap-3">
          {pic ? (
            <img
              src={pic}
              alt={u.fullName || "Profile"}
              className={`h-9 w-9 rounded-full object-cover ring-1 ring-gray-200 ${
                isChampion ? "shadow-[0_0_0_3px_rgba(250,204,21,0.5)]" : ""
              }`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700 ${
                isChampion ? "ring-2 ring-yellow-400" : ""
              }`}
            >
              {String(u.fullName || "?")
                .split(" ")
                .map((s) => s[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}

          <div className="flex items-center">
            <div className={`font-medium ${isChampion ? "text-yellow-900" : "text-gray-900"}`}>
              {u.fullName || "—"}
            </div>
            {showRank && rankBadge(u._rank)}
          </div>
        </div>

        <div className="pl-12 text-xs text-gray-500">{u.email || ""}</div>
      </td>

      {/* Other columns */}
      <td className="px-4 py-3 text-right font-semibold text-emerald-600">
        {formatBHD(u.paidTotal)}
      </td>
      <td className="px-4 py-3 text-right font-semibold text-sky-600">
        {formatBHD(u.unpaidTotal)}
      </td>
      <td className="px-4 py-3 text-right">
        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
          {u.expiredCount}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-gray-800">
        {formatBHD(u.paidTotalWithoutVAT)}
      </td>
      <td className="px-4 py-3 text-right text-gray-800">
        {formatBHD(u.unpaidTotalWithoutVAT)}
      </td>
      <td className="px-4 py-3 text-right text-gray-700">{u.paidCount}</td>
      <td className="px-4 py-3 text-right text-gray-700">{u.unpaidCount}</td>
    </tr>
  );
}

/** Compact KPI card for Digital Channels (no rankings, no effects) */
function DigitalUserCard({ u }) {
  const pic = buildPicUrl(u.profilePic || u.ProfilePic);
  const paid = Number(u.paidTotal || 0);
  const unpaid = Number(u.unpaidTotal || 0);
  const total = Math.max(paid + unpaid, 0.00001);
  const paidPct = Math.min(100, Math.round((paid / total) * 100));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        {pic ? (
          <img
            src={pic}
            alt={u.fullName || "Profile"}
            className="h-10 w-10 rounded-full object-cover ring-1 ring-gray-200"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
            {String(u.fullName || "?")
              .split(" ")
              .map((s) => s[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-medium text-gray-900">{u.fullName || "—"}</div>
          <div className="text-xs text-gray-500">{u.email || ""}</div>
        </div>
      </div>

      {/* Progress */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Paid vs Total</span>
          <span>{paidPct}%</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-2 rounded-full bg-emerald-500 transition-all"
            style={{ width: `${paidPct}%` }}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-emerald-50 p-2">
          <div className="text-[11px] text-emerald-700">Paid (WITH VAT)</div>
          <div className="font-semibold text-emerald-700">{formatBHD(u.paidTotal)}</div>
        </div>
        <div className="rounded-lg bg-sky-50 p-2">
          <div className="text-[11px] text-sky-700">Unpaid (WITH VAT)</div>
          <div className="font-semibold text-sky-700">{formatBHD(u.unpaidTotal)}</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-[11px] text-gray-600">Paid (No VAT)</div>
          <div className="font-semibold text-gray-800">{formatBHD(u.paidTotalWithoutVAT)}</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-[11px] text-gray-600">Unpaid (No VAT)</div>
          <div className="font-semibold text-gray-800">{formatBHD(u.unpaidTotalWithoutVAT)}</div>
        </div>
      </div>

      {/* Counts */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
          # Paid: {u.paidCount ?? 0}
        </span>
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
          # Unpaid: {u.unpaidCount ?? 0}
        </span>
        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700">
          Expired: {u.expiredCount ?? 0}
        </span>
      </div>
    </div>
  );
}

export default function DepartmentMonthLeaderboard({ toast }) {
  const token = sessionStorage.getItem("token") || "";
  const ownerOf = useMemo(() => getOwnerOfFromSession(), []);
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1..12
  const [department, setDepartment] = useState(() =>
    ownerOf.length === 1 ? ownerOf[0] : ""
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setApiError(false);

      const res = await GetDepartmentMonthSummary(
        token,
        year,
        month,
        toast,
        undefined,
        { department: department || undefined } // pass only if chosen
      );

      const valid = res && typeof res === "object" && Array.isArray(res.users);
      if (valid) {
        setData(res);
      } else {
        setData(null);
        setApiError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, month, department]); // include department so Apply/Change refetches

  const dept = data;

  // Detect Digital Channels (selected or from API)
  const selectedDeptName = (department || dept?.departmentName || "").toString();
  const isDigital = useMemo(() => {
    const s = selectedDeptName.trim().toLowerCase();
    if (!s) return false;
    // matches "digital channels" or similar phrasing
    return s.includes("digital") && s.includes("channel");
  }, [selectedDeptName]);

  // rank client-side (for non-digital only): Paid desc, Unpaid asc
  const rankedRows = useMemo(() => {
    const rows = dept?.users ?? [];
    const sorted = [...rows].sort((a, b) => {
      const paidDiff = Number(b.paidTotal || 0) - Number(a.paidTotal || 0);
      if (paidDiff !== 0) return paidDiff;
      return Number(a.unpaidTotal || 0) - Number(b.unpaidTotal || 0);
    });
    return sorted.map((u, idx) => ({ ...u, _rank: idx + 1 }));
  }, [dept]);

  const tableRef = useRef(null);

  // If API errored, show only the filters + retry (no report)
  if (apiError) {
    return (
      <div className="mt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold text-gray-900">
              {isDigital ? "Digital Channels — Performance" : "Department Leaderboard"}
            </h3>

            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Year"
            >
              {Array.from({ length: 6 }).map((_, i) => {
                const y = now.getFullYear() - i;
                return (
                  <option key={y} value={y}>
                    {y}
                  </option>
                );
              })}
            </select>

            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Month"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            {ownerOf.length > 0 && (
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Select a department you manage. Leave blank for your own department."
              >
                <option value="">(My department)</option>
                {ownerOf.map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={load}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 active:scale-[0.99]"
            >
              Retry
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-dashed border-red-300 bg-white p-6 text-sm text-red-700">
          Couldn’t load the department summary. Please adjust filters and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">
            {isDigital ? "Digital Channels — Performance" : "Department Leaderboard"}
          </h3>

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            title="Year"
          >
            {Array.from({ length: 6 }).map((_, i) => {
              const y = now.getFullYear() - i;
              return (
                <option key={y} value={y}>
                  {y}
                </option>
              );
            })}
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            title="Month"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>

          {ownerOf.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Select a department you manage. Leave blank for your own department."
              >
                <option value="">(My department)</option>
                {ownerOf.map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
              </select>

              <button
                onClick={load}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 active:scale-[0.99]"
              >
                Apply
              </button>
            </div>
          )}

          {dept && (
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-700">
              <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Paid: {formatBHD(dept.departmentPaidTotal)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                Unpaid: {formatBHD(dept.departmentUnpaidTotal)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                Expired: {dept.departmentExpiredCount ?? 0}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={load}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 active:scale-[0.99]"
        >
          Refresh
        </button>
      </div>

      {/* Totals cards */}
      {dept && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-gray-500">Paid (Gross)</div>
            <div className="text-lg font-semibold text-gray-800">{formatBHD(dept.departmentPaidTotal)}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-gray-500">Unpaid (Gross)</div>
            <div className="text-lg font-semibold text-gray-800">{formatBHD(dept.departmentUnpaidTotal)}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-gray-500">Paid (Without VAT)</div>
            <div className="text-lg font-semibold text-gray-800">{formatBHD(dept.departmentPaidTotalWithoutVAT)}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-gray-500">Unpaid (Without VAT)</div>
            <div className="text-lg font-semibold text-gray-800">{formatBHD(dept.departmentUnpaidTotalWithoutVAT)}</div>
          </div>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center p-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : !dept ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No data available.
        </div>
      ) : isDigital ? (
        // ---------- DIGITAL CHANNELS LAYOUT ----------
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(dept.users ?? []).map((u) => (
            <DigitalUserCard key={u.userId} u={u} />
          ))}
        </div>
      ) : (
        // ---------- DEFAULT LEADERBOARD (unchanged) ----------
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table ref={tableRef} className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-right">Paid (WITH VAT)</th>
                <th className="px-4 py-3 text-right">Unpaid (WITH VAT)</th>
                <th className="px-4 py-3 text-right">Expired</th>
                <th className="px-4 py-3 text-right">Paid (No VAT)</th>
                <th className="px-4 py-3 text-right">Unpaid (No VAT)</th>
                <th className="px-4 py-3 text-right"># Paid</th>
                <th className="px-4 py-3 text-right"># Unpaid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {rankedRows.map((u) => (
                <LeaderboardRow key={u.userId} u={u} tableRef={tableRef} />
              ))}
            </tbody>

            {/* Footer row totals */}
            <tfoot className="bg-gray-50">
              <tr className="text-sm font-semibold text-gray-700">
                <td className="px-4 py-3 text-right">Totals:</td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatBHD(dept.departmentPaidTotal)}</td>
                <td className="px-4 py-3 text-right text-sky-700">{formatBHD(dept.departmentUnpaidTotal)}</td>
                <td className="px-4 py-3 text-right">{dept.departmentExpiredCount}</td>
                <td className="px-4 py-3 text-right">{formatBHD(dept.departmentPaidTotalWithoutVAT)}</td>
                <td className="px-4 py-3 text-right">{formatBHD(dept.departmentUnpaidTotalWithoutVAT)}</td>
                <td className="px-4 py-3 text-right">{dept.departmentPaidCount}</td>
                <td className="px-4 py-3 text-right">{dept.departmentUnpaidCount}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
